import React, {ReactElement} from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {TreeItem} from '@mui/x-tree-view/TreeItem';
import {TreeView} from '@mui/x-tree-view/TreeView';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import {TreeViewItem} from '../../../../@types/tree-view-item';
import {resourceIcon} from '../../../../utils/resource-icons';
import LinearProgress from '@mui/material/LinearProgress';
import {ResourceType} from '../../../../@types/resource-type';
import SafetyService from '../../../../services/safety.service';
import {fakePage} from '../../../../@types/spring-data-page';
import {safetyModelsTreeItems} from '../../../../utils/safety-model-utils';
import {toast} from 'react-toastify';
import {PsfLinksProps} from '../../../../@types/editor-props';
import {generateTreeItems} from '../../../../utils/resource-utils';
import EventSequenceSummary from '../../../../@types/event-sequence-summary';
import Checkbox from '@mui/material/Checkbox';
import {groupRoles} from '../../../../utils/role-to-rights';
import authService from '../../../../services/auth.service';
import {isNotEditable} from '../../../../utils/psf-template-utils';
import PsfTemplateService from '../../../../services/psf-template.service';
import {IconButton, Tooltip} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import {isNumeric} from '../../../../utils/utils';

const noEventSequenceNode: TreeViewItem = {
    id: '-1',
    object: '',
    name: 'No Event Sequences in this Safety Model exist to select from',
    type: ResourceType.UNKNOWN
};

const selectAllNode: TreeViewItem = {
    id: 'selectAll',
    object: '',
    name: 'Select All',
    type: ResourceType.EVENT_SEQUENCES,
    groupRole: groupRoles.admin
};

export function PsfTemplateEventSequenceLinks(props: PsfLinksProps): ReactElement {
    const { title, groups, esdLinks, psfTemplate, updateLinks} = props;
    const [expanded, setExpanded] = React.useState<string[]>([]);
    const [linkable, setLinkable] = React.useState<boolean>(false);
    const [treeViewItems, setTreeViewItems] = React.useState<TreeViewItem[]>([]);
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    const [existingLinks, setExistingLinks] = React.useState<any>({}); //a dictionary with linked event sequence ids as keys and linked psf template and group as values
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    const [expandedGroups, setExpandedGroups] = React.useState<string[]>([]);
    const [loadingChildren, setLoadingChildren] = React.useState<boolean>(false);

    React.useEffect(() => {
        setTreeViewItems(generateTreeItems(groups, ResourceType.GROUPS));
    }, [groups])

    React.useEffect(() => {
        setLinkable(!isNotEditable(psfTemplate, authService.isSuperUser()));
    }, [psfTemplate])

    const handleSingleClick = (nodeId: string): void => {
        let resourceInfo = nodeId.split('-', 2);
        if (resourceInfo.length === 2) {
            const node = findTreeViewItemByTypeAndId(treeViewItems, resourceInfo[0], resourceInfo[1]);
            if (node && (!node.children || node.children.find((item) => item.type !== node.type) === undefined)) {
                loadChildren(resourceInfo[1], resourceInfo[0], node, nodeId);
            }
        }
    }

    const findTreeViewItemByTypeAndId = (items: TreeViewItem[], type: string, id: string): TreeViewItem|undefined => {
        let node = items.find((item) => item.type === type && item.id === id);
        if (node === undefined) {
            items.forEach((item) => {
                if (node === undefined && item.children) {
                    node = findTreeViewItemByTypeAndId(item.children, type, id);
                    if (node !== undefined) { return node; }
                }
            });
        }
        return node;
    }

    const loadChildren = (parentId: string, type: string, parentNode: TreeViewItem, nodeId: string): void => {
        if (!parentNode.children) parentNode.children = [] as TreeViewItem[];
        if (parentNode.type === ResourceType.GROUPS) {
            const fetchSafetyModelsPromise: Promise<any> | null = SafetyService.fetchPagedSafetyModels(fakePage, parentId);
            if (fetchSafetyModelsPromise) {
                setLoadingChildren(true);
                fetchSafetyModelsPromise.then((safetyModels) => {
                    if (safetyModels.content) {
                        const smTreeItems = safetyModelsTreeItems(safetyModels.content);
                        parentNode.children?.push(...smTreeItems);
                    }
                    if (!expanded.includes(parentNode.id)) { setExpanded([...expanded, nodeId]); }
                }).catch((error) => {
                    toast.error('Failed to fetch safety models for group ' + parentId, error.message);
                }).finally(() => {
                    setLoadingChildren(false);
                });
            }
            if (!expandedGroups.includes(parentId)) {
                PsfTemplateService.fetchLinksByGroupId(parentId).then((esLinks) => {
                    expandedGroups.push(parentId);
                    esLinks.forEach((link) => {
                        if (esdLinks.findIndex((esdId) => esdId == link.eventSequenceId.toString()) < 0) {
                            existingLinks[link.eventSequenceId.toString()] = {psfTemplateName: link.psfTemplateName, group: link.group.name}
                        }
                    });
                }).catch((error) => {
                    toast.error('Failed to fetch PSF event sequence links for group ' + parentId, error.message);
                }).finally(() => {
                    setLoadingChildren(false);
                });
            }
        } else if (parentNode.type === ResourceType.SAFETY_MODELS) {
            setLoadingChildren(true);
            SafetyService.getEventSequences(parentId).then((ess: EventSequenceSummary[]) => {
                const esTreeItems: TreeViewItem[] = ess.map((es) => {
                    if (parentNode.groupRole) es.role = parentNode.groupRole;
                    return {
                        id: es.id!.toString(),
                        object: es,
                        name: es.uniqueId + ' - ' + es.name,
                        type: ResourceType.EVENT_SEQUENCES,
                        groupRole: es.role
                    }
                })
                if (esTreeItems && esTreeItems.length > 0) {
                    if (linkableToNode(parentNode.groupRole)) {
                        parentNode.children?.push({...selectAllNode, id: 'selectAll-'+parentNode.id});
                    }
                    parentNode.children?.push(...esTreeItems);
                } else {
                    parentNode.children?.push(noEventSequenceNode);
                }
                if (!expanded.includes(parentNode.id)) { setExpanded([...expanded, nodeId]); }
            }).catch((error) => {
                toast.error(error.message);
            }).finally(() => {
                setLoadingChildren(false);
            });
        }
    }

    const linkableToNode = (roleInNode: string|undefined): boolean => {
        return authService.isSuperUser() || (roleInNode?.toUpperCase() === groupRoles.admin && linkable);
    }

    const linkNode = (...esdIds: string[]): void => {
        esdLinks.push(...esdIds);
        updateLinks(Object.assign([], esdLinks));
    }

    const createTooltip = (psfTemplateName: string, groupName: string): string => {
        return `This event sequence has already been linked in another performance shaping factor template: ${psfTemplateName}, in the group ${groupName}. Each event sequence may only be linked once.`;
    }

    const isEventSequenceLinked = (nodeId: string): boolean => {
        let linked = false;
        if (nodeId.startsWith('selectAll')) {
            let parentNode = findTreeViewItemByTypeAndId(treeViewItems, ResourceType.SAFETY_MODELS, nodeId.split('-')[1]);
            if (parentNode && parentNode.children) {
                //add 'selectAll' to the checked id list if all children are linked
                let unlinkedNode = parentNode.children.find((node) =>
                    isNumeric(node.id) &&
                    esdLinks.findIndex((esdId) => esdId == node.id) < 0 &&
                    existingLinks[node.id] === undefined);
                linked = unlinkedNode === undefined;
            }
        } else {
            linked = esdLinks.findIndex((esdId) => esdId == nodeId) >= 0;
        }
        return linked;
    }

    const renderTreeItem = (node: TreeViewItem): ReactElement => {
        return (
            <TreeItem tabIndex={0} id={'treeitem-' + node.type + '-' + node.id} key={node.type + '-' + node.id} nodeId={ node.type + '-' + node.id} label={
                <Box tabIndex={0} id={'box-' + node.type + '-' + node.id} sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5, pr: 0 }}>
                    {node.type === ResourceType.EVENT_SEQUENCES && existingLinks[node.id] === undefined &&
                        <Checkbox aria-label='link-esd' size='small' checked={isEventSequenceLinked(node.id)} disabled={!linkableToNode(node.groupRole)}
                            onClick={(): void => {
                                if (node.id.startsWith('selectAll')) {
                                    let parentNode = findTreeViewItemByTypeAndId(treeViewItems, ResourceType.SAFETY_MODELS, node.id.split('-')[1]);
                                    if (parentNode && parentNode.children) {
                                        isEventSequenceLinked(node.id) ?
                                            updateLinks(esdLinks.filter((esdId) => parentNode!.children!.findIndex((child) => child.id == esdId) < 0)) :
                                            linkNode(...parentNode.children!.map((child) => child.id).filter((id) => existingLinks[id] === undefined))
                                    }
                                } else {
                                    isEventSequenceLinked(node.id) ?
                                        updateLinks(esdLinks.filter((esdId) => esdId != node.id)) :
                                        linkNode(node.id)
                                }
                            }} />}
                    {node.type === ResourceType.EVENT_SEQUENCES && existingLinks[node.id] &&
                        <Tooltip title={createTooltip(existingLinks[node.id].psfTemplateName, existingLinks[node.id].group)}>
                            <IconButton color='primary' aria-label='Link Info'><InfoIcon /></IconButton>
                        </Tooltip>}
                    {node.type !== ResourceType.UNKNOWN && !isNaN(parseInt(node.id)) && <Typography id={'icon-' + node.type + '-' + node.id} sx={{ mr: 1 }} component="div" >{resourceIcon(node.type)}</Typography>}
                    <Typography id={'name-' + node.type + '-' + node.id} variant="body2" sx={{ flexGrow: 1 }}>{node.name}</Typography>
                    {node.type === ResourceType.GROUPS && <Typography variant="caption" sx={{ mr: 1, mt: 0.5, color: '#606060' }}>
                        {node.groupPath ? node.groupPath : null}
                    </Typography> &&
                    <Typography variant="caption" sx={{ mr: 1 }}>
                        {node.groupRole ? <Chip label={node.groupRole.toUpperCase()} variant="outlined" size="small" sx={{ color: '#606060', fontSize: '8px' }}/> : null}
                    </Typography>}
                </Box>
            }
            icon={node.type === ResourceType.GROUPS || node.type === ResourceType.SAFETY_MODELS ? <ChevronRightIcon id={'toggle-' + node.type + '-' + node.id} /> : null}
            >
                {Array.isArray(node.children) ? node.children?.map((child) => renderTreeItem(child)) : null}
            </TreeItem>
        );
    }

    return (
        <>
            {title? <Typography variant='h6' color='primary'>{title}</Typography> : null}
            <TreeView
                aria-label="tree-view"
                defaultCollapseIcon={<ExpandMoreIcon aria-label='collapse icon' />}
                defaultExpandIcon={<ChevronRightIcon aria-label='expand icon'/>}
                sx={{ height: '580px', mt: '20px', flexGrow: 1, overflowY: 'auto', width: '100%', overflowX: 'auto' }}
                expanded={expanded}
                onNodeToggle={(_event: React.SyntheticEvent, nodeIds: string[]): void => {
                    setExpanded(nodeIds);
                }}
                onNodeSelect={(_event: React.SyntheticEvent, nodeId: string): void => {
                    handleSingleClick(nodeId);
                }}
            >
                {treeViewItems.map((node) => renderTreeItem(node))}
            </TreeView>
            {loadingChildren &&
                <Box sx={{ width: '100%' }}>
                    <LinearProgress aria-label={loadingChildren? 'progress bar': undefined} />
                </Box>
            }
        </>
    );
}